import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ShieldCheck,
  User,
  Plus,
  PencilSimple,
  Trash,
  Crown,
  UserCircle,
} from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { API, AXIOS_SKIP_HTTP_CACHE } from '../config';
import { isAdmin, normalizeRole } from '../utils/roles';

const EMPTY_FORM = { name: '', email: '', password: '', role: 'user' };

function RoleBadge({ role, t }) {
  const admin = normalizeRole(role) === 'admin';
  return (
    <span
      className={
        admin
          ? 'inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100'
          : 'inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200'
      }
    >
      {admin ? <Crown size={14} weight="fill" /> : <UserCircle size={14} />}
      {admin ? t('roleAdmin') : t('roleUser')}
    </span>
  );
}

const UsersPage = () => {
  const { t } = useLanguage();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const loadUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, {
        withCredentials: true,
        ...AXIOS_SKIP_HTTP_CACHE,
      });
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error(t('failedFetchData'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (isAdmin(currentUser)) void loadUsers();
    else setLoading(false);
  }, [currentUser, loadUsers]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      name: row.name || '',
      email: row.email || '',
      password: '',
      role: normalizeRole(row.role),
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error(t('userName') + ' / ' + t('userEmail'));
      return;
    }
    if (!editing && (!form.password || form.password.length < 6)) {
      toast.error(t('userPassword'));
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const payload = {
          name: form.name.trim(),
          email: form.email.trim(),
          role: form.role,
        };
        if (form.password.trim()) payload.password = form.password.trim();
        await axios.put(`${API}/admin/users/${editing.id}`, payload, {
          withCredentials: true,
        });
        toast.success(t('userUpdatedSuccess'));
      } else {
        await axios.post(
          `${API}/admin/users`,
          {
            name: form.name.trim(),
            email: form.email.trim(),
            password: form.password,
            role: form.role,
          },
          { withCredentials: true },
        );
        toast.success(t('userCreatedSuccess'));
      }
      setDialogOpen(false);
      await loadUsers();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(
        typeof msg === 'string' ? msg : editing ? t('userUpdateFailed') : t('userCreateFailed'),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(t('confirmDeleteUser'))) return;
    try {
      await axios.delete(`${API}/admin/users/${row.id}`, { withCredentials: true });
      toast.success(t('userDeletedSuccess'));
      await loadUsers();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : t('userDeleteFailed'));
    }
  };

  const sortedUsers = useMemo(
    () =>
      [...users].sort((a, b) => {
        const ra = normalizeRole(a.role) === 'admin' ? 0 : 1;
        const rb = normalizeRole(b.role) === 'admin' ? 0 : 1;
        if (ra !== rb) return ra - rb;
        return (a.name || '').localeCompare(b.name || '');
      }),
    [users],
  );

  if (!isAdmin(currentUser)) {
    return (
      <div className="animate-fadeIn flex items-center justify-center min-h-[40vh]">
        <p className="text-[#64748b]">{t('adminOnlyPage')}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#00CBA9] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="animate-fadeIn" data-testid="users-page">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <ShieldCheck size={28} weight="duotone" className="text-[#00CBA9]" />
            {t('users')}
          </h1>
          <p className="page-subtitle">{t('usersPageSubtitle')}</p>
        </div>
        <button type="button" className="vmk-btn vmk-btn-primary shrink-0" onClick={openCreate}>
          <Plus size={18} weight="bold" />
          {t('addUser')}
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-8">
        <div className="vmk-card border-l-4 border-l-amber-400">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-950/50 flex items-center justify-center shrink-0">
              <Crown size={22} className="text-amber-700 dark:text-amber-300" weight="duotone" />
            </div>
            <div>
              <h3 className="font-semibold text-[#1a1a1a] dark:text-white">{t('roleAdmin')}</h3>
              <p className="text-sm text-[#64748b] mt-1">{t('roleAdminDesc')}</p>
            </div>
          </div>
        </div>
        <div className="vmk-card border-l-4 border-l-slate-300 dark:border-l-slate-600">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0">
              <User size={22} className="text-slate-600 dark:text-slate-300" weight="duotone" />
            </div>
            <div>
              <h3 className="font-semibold text-[#1a1a1a] dark:text-white">{t('roleUser')}</h3>
              <p className="text-sm text-[#64748b] mt-1">{t('roleUserDesc')}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="table-container">
        <table className="vmk-table">
          <thead>
            <tr>
              <th>{t('userName')}</th>
              <th>{t('userEmail')}</th>
              <th>{t('userRole')}</th>
              <th className="text-right">{t('actions')}</th>
            </tr>
          </thead>
          <tbody>
            {sortedUsers.map((row) => (
              <tr key={row.id}>
                <td className="font-medium">{row.name}</td>
                <td className="text-[#64748b]">{row.email}</td>
                <td>
                  <RoleBadge role={row.role} t={t} />
                </td>
                <td className="table-actions-cell">
                  <div className="table-row-actions">
                    <button
                      type="button"
                      className="vmk-btn vmk-btn-icon"
                      onClick={() => openEdit(row)}
                      title={t('editUser')}
                      aria-label={t('editUser')}
                    >
                      <PencilSimple size={16} weight="bold" />
                    </button>
                    <button
                      type="button"
                      className="vmk-btn vmk-btn-icon vmk-btn-icon-warn"
                      onClick={() => void handleDelete(row)}
                      disabled={String(row.id) === String(currentUser?.id)}
                      title={t('deleteUser')}
                      aria-label={t('deleteUser')}
                    >
                      <Trash size={16} weight="bold" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? t('editUser') : t('addUser')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                {t('userName')}
              </label>
              <input
                className="vmk-input mt-1 w-full"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                {t('userEmail')}
              </label>
              <input
                type="email"
                className="vmk-input mt-1 w-full"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                {editing ? t('userPasswordOptional') : t('userPassword')}
              </label>
              <input
                type="password"
                className="vmk-input mt-1 w-full"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                autoComplete="new-password"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide mb-2 block">
                {t('userRole')}
              </label>
              <div className="grid grid-cols-2 gap-2">
                {['admin', 'user'].map((roleKey) => {
                  const selected = form.role === roleKey;
                  const admin = roleKey === 'admin';
                  return (
                    <button
                      key={roleKey}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, role: roleKey }))}
                      className={`flex flex-col items-start gap-1 rounded-xl border-2 p-3 text-left transition-all ${
                        selected
                          ? admin
                            ? 'border-amber-400 bg-amber-50 dark:bg-amber-950/30'
                            : 'border-[#00CBA9] bg-[#00CBA9]/5'
                          : 'border-[#e2e8f0] dark:border-[#334155] hover:border-[#00CBA9]/40'
                      }`}
                    >
                      <span className="font-semibold text-sm">
                        {admin ? t('roleAdmin') : t('roleUser')}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
            <button
              type="button"
              className="vmk-btn vmk-btn-primary w-full"
              disabled={saving}
              onClick={() => void handleSave()}
            >
              {saving ? '…' : editing ? t('editUser') : t('addUser')}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
